import { useEffect, useMemo, useState } from 'react';
import type { Data } from '../types';
import { createEmptyHitgroupValues, type HitgroupKey, type HitgroupValues } from './DamageBodySvg';
import DamageHitgroupMap from './DamageHitgroupMap';

type DamageHitgroupPanelProps = {
  data: Data | null;
};

type Mode = 'given' | 'taken';

const normalizeHitgroupKey = (raw: string): HitgroupKey | null => {
  const compact = raw.toLowerCase().replace(/[\s_-]+/g, '');

  switch (compact) {
    case 'head':
      return 'head';
    case 'chest':
      return 'chest';
    case 'stomach':
      return 'stomach';
    case 'leftarm':
      return 'left arm';
    case 'rightarm':
      return 'right arm';
    case 'leftleg':
      return 'left leg';
    case 'rightleg':
      return 'right leg';
    default:
      return null;
  }
};

const isGenericHitgroup = (raw: string) =>
  raw.toLowerCase().replace(/[\s_-]+/g, '') === 'generic';

const extractGenericAmount = (source: Record<string, number>) => {
  let total = 0;
  for (const [rawKey, amount] of Object.entries(source)) {
    if (!isGenericHitgroup(rawKey)) continue;
    if (typeof amount !== 'number' || Number.isNaN(amount)) continue;
    total += amount;
  }
  return total;
};

const applyHitgroupValues = (
  source: Record<string, number>,
  values: HitgroupValues,
) => {
  let total = 0;

  for (const [rawKey, amount] of Object.entries(source)) {
    const key = normalizeHitgroupKey(rawKey);
    if (!key) continue;
    if (typeof amount !== 'number' || Number.isNaN(amount)) continue;
    values[key] += amount;
    total += amount;
  }

  return total;
};

const buildHitgroupStats = (
  data: Data | null,
  playerName: string,
  mode: Mode,
) => {
  const shotValues = createEmptyHitgroupValues();
  const damageValues = createEmptyHitgroupValues();
  if (!data || !playerName) {
    return {
      shotValues,
      damageValues,
      totalShots: 0,
      totalDamage: 0,
      utilityShots: 0,
      utilityDamage: 0,
    };
  }

  const stats = data.playerstats[playerName];
  if (!stats) {
    return {
      shotValues,
      damageValues,
      totalShots: 0,
      totalDamage: 0,
      utilityShots: 0,
      utilityDamage: 0,
    };
  }

  const damageByHitgroup =
    mode === 'given' ? stats.damage.given.hitgroup.damage : stats.damage.taken.hitgroup.damage;
  const shotsByHitgroup =
    mode === 'given' ? stats.damage.given.hitgroup.shots : stats.damage.taken.hitgroup.shots;

  const totalShots = applyHitgroupValues(shotsByHitgroup, shotValues);
  const totalDamage = applyHitgroupValues(damageByHitgroup, damageValues);
  const utilityShots = extractGenericAmount(shotsByHitgroup);
  const utilityDamage = extractGenericAmount(damageByHitgroup);

  return { shotValues, damageValues, totalShots, totalDamage, utilityShots, utilityDamage };
};

export default function DamageHitgroupPanel({ data }: DamageHitgroupPanelProps) {
  const [mode, setMode] = useState<Mode>('given');
  const playerNames = useMemo(
    () => (data ? Object.keys(data.playerstats).sort((a, b) => a.localeCompare(b)) : []),
    [data],
  );
  const [selectedPlayer, setSelectedPlayer] = useState('');

  useEffect(() => {
    if (playerNames.length === 0) {
      setSelectedPlayer('');
      return;
    }

    if (!playerNames.includes(selectedPlayer)) {
      setSelectedPlayer(playerNames[0]);
    }
  }, [playerNames, selectedPlayer]);

  const { shotValues, damageValues, totalShots, totalDamage, utilityShots, utilityDamage } =
    buildHitgroupStats(
      data,
      selectedPlayer,
      mode,
    );
  const hasData = playerNames.length > 0;
  const combinedDamage = totalDamage + (utilityDamage ?? 0);
  const utilityShare =
    combinedDamage > 0 ? Math.round(((utilityDamage ?? 0) / combinedDamage) * 100) : 0;
  const utilityToneClass = mode === 'given' ? 'text-accent' : 'text-red-500';
  const utilityBarClass = mode === 'given' ? 'bg-accent/80' : 'bg-red-500/80';

  return (
    <div
      className={`relative col-span-6 overflow-hidden rounded-[20px] border border-stroke bg-card p-5 shadow-[0_18px_32px_rgba(0,0,0,0.45)] animate-rise ${
        !hasData ? 'opacity-70' : ''
      } max-[960px]:col-span-1`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[18px] font-semibold">Damage heatmap</h3>
          <p className="mt-1 text-[13px] text-muted">
            Hitgroup breakdown by shots, with damage totals for context
          </p>
        </div>
        <span className="rounded-full border border-stroke bg-[rgba(255,255,255,0.04)] px-2.5 py-1 text-[12px] font-semibold text-muted">
          {mode === 'given' ? 'Given' : 'Received'}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-3">
        <label className="grid gap-1 text-[12px] uppercase tracking-[1px] text-muted">
          <select
            className="w-[220px] rounded-xl border border-stroke bg-[#210E1C] px-3 py-2 text-[13px] text-ink outline-none transition focus:border-accent disabled:cursor-not-allowed disabled:opacity-60"
            value={selectedPlayer}
            onChange={(event) => setSelectedPlayer(event.target.value)}
            disabled={!hasData}
          >
            {playerNames.map((name) => (
              <option key={name} value={name} className="text-ink">
                {name}
              </option>
            ))}
          </select>
        </label>

        <div className="inline-flex items-center gap-1 rounded-full border border-stroke bg-[rgba(31,13,26,0.8)] p-1">
          <button
            className={`rounded-full px-3 py-2 text-[12px] font-semibold transition ${
              mode === 'given'
                ? 'bg-accent text-bg shadow-[0_8px_18px_rgba(255,254,62,0.25)]'
                : 'text-muted hover:text-ink'
            }`}
            type="button"
            onClick={() => setMode('given')}
            aria-pressed={mode === 'given'}
            disabled={!hasData}
          >
            Damage dealt
          </button>
          <button
            className={`rounded-full px-3 py-2 text-[12px] font-semibold transition ${
              mode === 'taken'
                ? 'bg-red-500 text-bg shadow-[0_8px_18px_rgba(239,68,68,0.25)]'
                : 'text-muted hover:text-ink'
            }`}
            type="button"
            onClick={() => setMode('taken')}
            aria-pressed={mode === 'taken'}
            disabled={!hasData}
          >
            Damage received
          </button>
        </div>
      </div>

      <div className="mt-2">
        {mode === 'given' ? (
          <DamageHitgroupMap
            label="Damage given"
            shots={shotValues}
            damage={damageValues}
            totalShots={totalShots}
            totalDamage={totalDamage}
            baseColor="255, 254, 62"
            toneClass="border-accent/40 bg-accent/20 text-accent"
          />
        ) : (
          <DamageHitgroupMap
            label="Damage received"
            shots={shotValues}
            damage={damageValues}
            totalShots={totalShots}
            totalDamage={totalDamage}
            baseColor="239, 68, 68"
            toneClass="border-red-500/40 bg-red-500/20 text-red-500"
          />
        )}

        <div className="mt-4 rounded-2xl border border-stroke bg-[rgba(31,13,26,0.6)] p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[1px] text-muted">
                Utility damage
              </div>
              <div className={`mt-2 text-[20px] font-semibold ${utilityToneClass}`}>
                {Math.round((utilityDamage ?? 0))} dmg
              </div>
            </div>
            <div className="text-right text-[11px] text-muted">
              <div>{Math.round(utilityShots ?? 0)} hits</div>
              <div>{utilityShare}% of total dmg</div>
            </div>
          </div>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[rgba(60,27,46,0.7)]">
            <div
              className={`h-full rounded-full ${utilityBarClass}`}
              style={{ width: `${utilityShare}%` }}
            />
          </div>
        </div>
      </div>

      {!hasData && (
        <div className="absolute inset-4 grid place-items-center rounded-2xl border border-dashed border-accent/35 bg-[rgba(31,13,26,0.92)] text-center text-[14px] font-semibold text-muted">
          Upload a log file to see hitgroup shots.
        </div>
      )}
    </div>
  );
}
